import axios from "axios"
import { portalGet, portalPost, portalPut } from "@/api/consumer/client"
import type { AuthResponse, User } from "@/api/shared/types"

export interface PortalApiError {
  code: string
  message: string
}

export interface PortalRegisterResponse {
  message: string
  user_id: string
  email: string
  requires_email_verification?: boolean
}

export interface PortalPricingPlan {
  id: string
  code: string
  name: string
  price_idr: number
  strike_price_idr: number
  period_days: number
  period: string
  kicker: string
  note: string
  foot: string
  features: string[]
  sort_order: number
  is_active: boolean
}

export interface PortalCoinPack {
  id: string
  code: string
  coins: number
  bonus_coins: number
  bonus_label: string
  price_idr: number
  sort_order: number
  is_active: boolean
}

export interface PortalBillingMe {
  balance_main: number
  balance_bonus: number
  balance_total: number
  has_active_subscription: boolean
  subscription?: {
    plan_id?: string
    plan_code: string
    plan_name: string
    starts_at: string
    ends_at: string
    status: string
  }
  scheduled_subscription?: {
    plan_id?: string
    plan_code: string
    plan_name: string
    starts_at: string
    ends_at: string
    status: string
  }
  default_unlock_coins: number
}

export async function portalGetPricingPlans(): Promise<{ plans: PortalPricingPlan[] }> {
  return portalGet("/portal/pricing/plans")
}

export async function portalGetCoinPacks(): Promise<{ coin_packs: PortalCoinPack[] }> {
  return portalGet("/portal/pricing/coin-packs")
}

export async function portalGetBillingMe(opts?: { sync?: boolean }): Promise<PortalBillingMe> {
  const q = opts?.sync ? "?sync=1" : ""
  return portalGet(`/portal/billing/me${q}`)
}

export interface PortalPaymentRow {
  id: string
  kind: string
  ref_id: string
  ref_label: string
  amount_idr: number
  external_id: string
  xendit_invoice_id?: string
  invoice_url?: string
  status: string
  paid_at?: string
  created_at: string
}

export interface PortalInvoice {
  id: string
  invoice_number: string
  kind: string
  kind_label: string
  ref_id: string
  ref_label: string
  amount_idr: number
  external_id: string
  xendit_invoice_id?: string
  xendit_invoice_url?: string
  status: string
  status_label: string
  paid_at?: string
  created_at: string
  customer_username: string
  customer_email: string
  customer_display_name: string
  issuer_name: string
  issuer_tagline: string
}

export async function portalGetBillingPayments(params?: {
  limit?: number
  offset?: number
}): Promise<{ payments: PortalPaymentRow[]; total: number }> {
  const limit = params?.limit ?? 20
  const offset = params?.offset ?? 0
  return portalGet(`/portal/billing/payments?limit=${limit}&offset=${offset}`)
}

export async function portalGetPaymentDetail(paymentId: string): Promise<PortalInvoice> {
  return portalGet(`/portal/billing/payments/${encodeURIComponent(paymentId)}`)
}

export async function portalGetPaymentInvoiceURL(
  paymentId: string,
): Promise<{ invoice_url: string }> {
  return portalGet(`/portal/billing/payments/${encodeURIComponent(paymentId)}/invoice`)
}

export async function portalBillingCheckout(body: {
  kind: "subscription" | "coin_pack"
  id: string
}): Promise<{ invoice_url: string; external_id: string }> {
  return portalPost("/portal/billing/checkout", body)
}

/** @deprecated pakai portalBillingCheckout */
export async function portalCreateXenditInvoice(planId: string): Promise<{ invoice_url: string }> {
  return portalPost<{ invoice_url: string }>("/portal/billing/xendit-invoice", { plan_id: planId })
}

export async function portalUnlockVideo(videoId: string): Promise<PortalBillingMe> {
  return portalPost("/portal/billing/unlock", { video_id: videoId })
}

function parsePortalApiError(error: unknown): PortalApiError {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { error?: string; message?: string } | undefined
    return {
      code: data?.error || "request_error",
      message: data?.message || "Terjadi kesalahan pada permintaan.",
    }
  }
  return {
    code: "request_error",
    message: "Terjadi kesalahan pada permintaan.",
  }
}

export async function portalLogin(body: {
  email: string
  password: string
}): Promise<AuthResponse> {
  try {
    return await portalPost<AuthResponse>("/portal/auth/login", body)
  } catch (error) {
    throw parsePortalApiError(error)
  }
}

export async function portalRegister(body: {
  username: string
  email: string
  password: string
}): Promise<PortalRegisterResponse> {
  try {
    return await portalPost("/portal/auth/register", body)
  } catch (error) {
    throw parsePortalApiError(error)
  }
}

export async function portalVerifyEmail(token: string): Promise<{ message: string }> {
  try {
    return await portalPost("/portal/auth/verify-email", { token })
  } catch (error) {
    throw parsePortalApiError(error)
  }
}

export async function portalResendVerificationEmail(email: string): Promise<{ message: string }> {
  try {
    return await portalPost("/portal/auth/resend-verification-email", { email })
  } catch (error) {
    throw parsePortalApiError(error)
  }
}

export async function portalLogout(): Promise<{ message: string }> {
  return portalPost("/portal/auth/logout", {})
}

export async function portalGetProfile(): Promise<User> {
  return portalGet<User>("/auth/profile")
}

export async function portalGetPreferences(): Promise<{ payload: string }> {
  return portalGet("/portal/me/preferences")
}

export async function portalPutPreferences(
  payload: string,
): Promise<{ message: string; payload: string }> {
  return portalPut("/portal/me/preferences", { payload })
}

export async function portalPutMarketingOptOut(
  optOut: boolean,
): Promise<{ marketing_opt_out: boolean }> {
  return portalPut("/portal/me/marketing", { opt_out: optOut })
}

export async function portalForgotPassword(
  _email: string,
): Promise<{ message: string; dev_reset_token?: string }> {
  try {
    return await portalPost("/portal/auth/forgot-password", { email: _email })
  } catch (error) {
    throw parsePortalApiError(error)
  }
}

export async function portalResetPassword(body: {
  token: string
  new_password: string
}): Promise<{ message: string }> {
  try {
    return await portalPost("/portal/auth/reset-password", body)
  } catch (error) {
    throw parsePortalApiError(error)
  }
}

export async function portalSaveWebFcmToken(token: string): Promise<{ message: string }> {
  return portalPut("/auth/web-fcm-token", { token })
}

export async function portalGoogleLogin(idToken: string): Promise<AuthResponse> {
  return portalPost<AuthResponse>("/portal/auth/google", { id_token: idToken })
}

export async function portalPutProfile(body: { username?: string; email?: string }): Promise<User> {
  try {
    return await portalPut<User>("/auth/profile", body)
  } catch (error) {
    throw parsePortalApiError(error)
  }
}

export async function portalUploadProfilePhoto(
  file: File,
): Promise<{ message: string; url: string }> {
  const formData = new FormData()
  formData.append("photo", file)
  try {
    return await portalPost("/auth/profile/photo", formData)
  } catch (error) {
    throw parsePortalApiError(error)
  }
}
